import React from 'react';
import { Search, CheckCircle, Calendar, XCircle, Clock } from 'lucide-react';
import { useUX } from '../contexts/UXContext';

/**
 * Responsive Candidate View Component
 * Adapts between table, grid, and card layouts based on UX negotiation
 */

const ResponsiveCandidateView = ({ 
  candidates, 
  loading, 
  onScreen, 
  onSchedule 
}) => {
  const { 
    uxFormat, 
    isMobile, 
    isTablet,
    getLayout,
    getDataGranularity 
  } = useUX();

  const layout = getLayout();
  const granularity = getDataGranularity();

  const getStatusColor = (status) => {
    switch(status) {
      case 'sourced': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'screened': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'scheduled': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'sourced': return <Search className="w-4 h-4" />;
      case 'screened': return <CheckCircle className="w-4 h-4" />;
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Card Layout (Mobile)
  const CardView = () => (
    <div className="space-y-4">
      {candidates.map(candidate => (
        <div 
          key={candidate.id} 
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {candidate.name}
              </h3>
              <p className="text-sm text-gray-500">{candidate.email}</p>
            </div>
            <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(candidate.current_status)}`}>
              {getStatusIcon(candidate.current_status)}
              <span className="capitalize">{candidate.current_status}</span>
            </span>
          </div>

          {/* Experience */}
          <div className="mb-3">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Experience:</span> {candidate.experience_years} years
            </p>
          </div>

          {/* Skills */}
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {(candidate.skills || []).slice(0, granularity === 'summary' ? 2 : 4).map((skill, idx) => (
                <span key={idx} className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                  {skill}
                </span>
              ))}
              {candidate.skills && candidate.skills.length > (granularity === 'summary' ? 2 : 4) && (
                <span className="px-2 py-1 text-xs text-gray-500">
                  +{candidate.skills.length - (granularity === 'summary' ? 2 : 4)} more
                </span>
              )}
            </div>
          </div>

          {/* Score */}
          {candidate.screening_score !== null && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">Match Score</span>
                <span className="text-sm font-semibold text-gray-900">{candidate.screening_score}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all ${candidate.screening_score >= 60 ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ width: `${candidate.screening_score}%` }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-2 pt-3 border-t border-gray-200">
            {candidate.current_status === 'sourced' && (
              <button
                onClick={() => onScreen(candidate.id)}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
              >
                Screen Candidate
              </button>
            )}
            {candidate.current_status === 'screened' && (
              <button
                onClick={() => onSchedule(candidate.id)}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
              >
                Schedule Interview
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Grid Layout (Tablet)
  const GridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {candidates.map(candidate => (
        <div 
          key={candidate.id} 
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {candidate.name}
              </h3>
              <p className="text-xs text-gray-500 truncate">{candidate.email}</p>
            </div>
            <span className={`ml-2 inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${getStatusColor(candidate.current_status)}`}>
              {getStatusIcon(candidate.current_status)}
              <span>{candidate.current_status}</span>
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Experience:</span>
              <span className="font-medium text-gray-900">{candidate.experience_years} years</span>
            </div>

            {candidate.screening_score !== null && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Score:</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 rounded-full h-1.5">
                    <div 
                      className={`h-1.5 rounded-full ${candidate.screening_score >= 60 ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ width: `${candidate.screening_score}%` }}
                    />
                  </div>
                  <span className="font-medium text-gray-900 w-10 text-right">{candidate.screening_score}%</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-1 pt-2">
              {(candidate.skills || []).slice(0, 3).map((skill, idx) => (
                <span key={idx} className="px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          <div className="flex space-x-2 mt-3 pt-3 border-t border-gray-200">
            {candidate.current_status === 'sourced' && (
              <button
                onClick={() => onScreen(candidate.id)}
                disabled={loading}
                className="flex-1 py-1.5 px-3 bg-indigo-600 text-white rounded text-xs font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                Screen
              </button>
            )}
            {candidate.current_status === 'screened' && (
              <button
                onClick={() => onSchedule(candidate.id)}
                disabled={loading}
                className="flex-1 py-1.5 px-3 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
              >
                Schedule
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Table Layout (Desktop)
  const TableView = () => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Candidate
            </th>
            {granularity !== 'summary' && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Skills
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Experience
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Score
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {candidates.map(candidate => (
            <tr key={candidate.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                  <div className="text-sm text-gray-500">{candidate.email}</div>
                </div>
              </td>
              {granularity !== 'summary' && (
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {(candidate.skills || []).slice(0, 3).map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {candidate.experience_years} years
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {candidate.screening_score !== null ? (
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className={`h-2 rounded-full ${candidate.screening_score >= 60 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${candidate.screening_score}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-900">{candidate.screening_score}</span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">-</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(candidate.current_status)}`}>
                  {getStatusIcon(candidate.current_status)}
                  <span className="capitalize">{candidate.current_status}</span>
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div className="flex space-x-2">
                  {candidate.current_status === 'sourced' && (
                    <button
                      onClick={() => onScreen(candidate.id)}
                      disabled={loading}
                      className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50 font-medium"
                    >
                      Screen
                    </button>
                  )}
                  {candidate.current_status === 'screened' && (
                    <button
                      onClick={() => onSchedule(candidate.id)}
                      disabled={loading}
                      className="text-green-600 hover:text-green-900 disabled:opacity-50 font-medium"
                    >
                      Schedule
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Empty state
  if (candidates.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">
          No candidates yet. Click "Source Candidates" to begin.
        </p>
      </div>
    );
  }

  // Render appropriate layout
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Candidate Pipeline</h2>
          {uxFormat && (
            <span className="text-xs text-gray-500">
              Layout: <span className="font-medium">{layout}</span>
              {granularity !== 'detailed' && (
                <> · View: <span className="font-medium">{granularity}</span></>
              )}
            </span>
          )}
        </div>
      </div>
      <div className="p-6">
        {layout === 'cards' ? <CardView /> : layout === 'grid' ? <GridView /> : <TableView />}
      </div>
    </div>
  );
};

export default ResponsiveCandidateView;

